import {
  Banner,
  DataTable,
  FlexBox,
  Icon,
  Page,
  Text,
} from '@dixa/lib-shared-habla';
import moment from 'moment-timezone';

import { flowsApi } from 'apis/flows';
import { AppLink } from 'components/appLink';
import { useAppPathParams } from 'utils/url/useAppPathParams';

const flowChannelNameMap = {
  phone: 'Phone',
  email: 'Email',
  chat: 'Chat',
  'dixa-messenger': 'Messenger',
};

type SettingsFlowAuditLogViewProps = {
  channel: keyof typeof flowChannelNameMap;
};

export function SettingsFlowAuditLogView({
  channel,
}: SettingsFlowAuditLogViewProps): JSX.Element {
  const { flowId } = useAppPathParams(
    `/settings/${channel}/flow/:flowId/history`,
  );
  const { flow, refetch, isError, isLoading } = flowsApi.useReadFlowQuery(
    { id: flowId },
    {
      selectFromResult: ({ data, ...rest }) => ({ flow: data?.data, ...rest }),
    },
  );

  return (
    <Page>
      <Page.Head
        title={
          <FlexBox gap="200" alignItems="center">
            <AppLink path={`/settings/${channel}`} style={{ color: 'inherit' }}>
              {flowChannelNameMap[channel]}
            </AppLink>

            <Icon name="angle-right" />

            {flow?.name}
          </FlexBox>
        }
      />

      <Page.Body>
        <Page.Section
          title="Audit log"
          callout={
            <Banner
              variant="info"
              description={
                <Text.Paragraph as="span" variant="4">
                  Any entries older than 3 months will be automatically deleted.
                </Text.Paragraph>
              }
            />
          }
        >
          <DataTable
            columns={[
              {
                label: 'Edit',
                selector: ({ updated_by }) => updated_by,
                style: {
                  width: '70%',
                },
              },
              {
                label: 'Date',
                selector: ({ updated_at }) => {
                  const time = moment(updated_at);
                  const localeData = moment.localeData();

                  return time.format(
                    `DD MMMM YYYY, ${localeData.longDateFormat('LT')}`,
                  );
                },
              },
            ]}
            data={flow?.updated_by_history ?? []}
            tableStatusProps={{
              loading: isLoading,
              loadingFailed: isError,
              onLoadingRetry: refetch,
              emptyState:
                !isLoading && !isError && flow?.updated_by_history?.length === 0
                  ? {
                      title: 'No data available',
                      description:
                        'We store data for 3 months. Any activity older than this will be deleted and not be displayed.',
                    }
                  : undefined,
            }}
          />
        </Page.Section>
      </Page.Body>
    </Page>
  );
}
